import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import Login from "./Authorization/Login";
import Register from "./Authorization/Register";
import ProtectedRoute from "./Authorization/ProtectedRoute";
import AdminRoute from "./Authorization/AdminRoute";
import Home from "./Main/Home";
import Navbar from "./Main/Navbar/Navbar";
import CentralitiesTable from "./AnalysisOfMembers/CentralitiesTable";
import Glos from "./Glossary/glossariymain";
import DatabaseAdmin from "./DatabaseAdmin/DatabaseAdmin";
import ParsingPage from "./Analysis_Parsing_Pages/AnalysisPages";
import AnalysisPage from "./Analysis_Parsing_Pages/ParsingPage";
import TextAnomalityAnalysis from "./Analysis_A/Text_Anomality_Analysis";
import TextMarkersAnalysis from "./Analysis_A/Text_Markers_Analysis";
import TopicsIntensityAnalysis from "./Analysis_A/Topics_Intensity_Analysis";
import ImageFilter from "./AnalysisOfDiscussions/ImageFilter"; // Добавляем новый компонент
import GlobalFiltersBar from "./components/GlobalFiltersBar";
import { getAPIUrl } from './api/apiUtils';
import TopicsTrendsPage from "./Analysis_A/TopicsTrendsPage";
import PredictionTrendsPage from "./Analysis_A/PredictionTrendsPage";
import DictionariesAdmin from "./DatabaseAdmin/DictionariesAdmin";
import DisscussionsAnalysis from "./pages/DisscussionsAnalysis";
import TopicsAnalysis from "./pages/TopicsAnalysis";
import EmotiveAnalysis from "./pages/EmotiveAnalysis";
import DynamicsAnalysis from "./pages/DynamicsAnalysis";
import Admin from "./pages/Admin";
import PostsSearch from "./pages/PostsSearch";
import { apiFetch, AUTH_EXPIRED_EVENT } from "./api/apiClient";
import { PAGE_KEYS, getAllowedPages, getFallbackPath } from "./utils/pageAccess";

// Функция для определения, должен ли отображаться GlobalFiltersBar на текущем маршруте
const shouldShowGlobalFiltersBar = (pathname) => {
  // Список маршрутов, на которых должен отображаться GlobalFiltersBar
  const routesWithFilters = [
    '/analyze',
    '/pol',
    '/',
    '/trager-dynamics',
    '/dynamics-context',
    '/season-index',
    '/time-series',
    '/diss',
    '/diss/community-activity',
    '/diss/anomaly',
    '/diss/markers',
    '/diss/disscussions',
    '/text-markers',
    '/image-filter',
    '/topics-analysis',
    '/emotive-analysis',

  ];
  
  return routesWithFilters.includes(pathname);
};

// Компонент-обертка для условного отображения GlobalFiltersBar
const ConditionalGlobalFiltersBar = ({ user, accessReady }) => {
  const location = useLocation();
  
  if (!user || !accessReady || !shouldShowGlobalFiltersBar(location.pathname)) {
    return null;
  }
  
  return <GlobalFiltersBar />;
};

const clearAuthStorage = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("deniedPages");
};

const SessionExpiredDialog = ({ isOpen, onConfirm }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm();
    navigate("/login", { replace: true });
  };

  return (
    <div className="session-expired-overlay" role="dialog" aria-modal="true" aria-labelledby="session-expired-title">
      <div className="session-expired-modal">
        <h2 id="session-expired-title">Нужно войти заново</h2>
        <p>Сессия истекла или токен больше не действителен. Авторизуйтесь снова, чтобы продолжить работу.</p>
        <button type="button" className="btn btn-primary session-expired-modal__button" onClick={handleConfirm} autoFocus>
          Перейти ко входу
        </button>
      </div>
    </div>
  );
};

const AccessDenied = ({ accessError }) => (
  <div className="top-level-page-container" style={{ padding: "32px" }}>
    <h1>Нет доступа к разделам</h1>
    <p>
      {accessError || "Для вашей учётной записи не осталось доступных разделов. Обратитесь к администратору."}
    </p>
  </div>
);

function App() {
  const [user, setUser] = useState(null);
  const [allowedPages, setAllowedPages] = useState([]);
  const [accessReady, setAccessReady] = useState(false);
  const [accessError, setAccessError] = useState("");
  const [isSessionExpired, setIsSessionExpired] = useState(false);

  console.log('App rendered, user:', user);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    setUser(storedUser);
  }, []);

  useEffect(() => {
    const handleAuthExpired = () => {
      clearAuthStorage();
      setUser(null);
      setAllowedPages([]);
      setAccessReady(false);
      setIsSessionExpired(true);
    };

    window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
  }, []);

  const handleSessionExpiredConfirm = () => {
    clearAuthStorage();
    setUser(null);
    setAllowedPages([]);
    setAccessReady(false);
    setIsSessionExpired(false);
  };

  useEffect(() => {
    let cancelled = false;
    const accessController = new AbortController();
    const accessTimeoutId = user
      ? window.setTimeout(() => accessController.abort(), 10000)
      : null;

    if (user && !user.id) {
      clearAuthStorage();
      setUser(null);
      setAllowedPages([]);
      setAccessError("");
      setAccessReady(false);
      setIsSessionExpired(true);
    } else if (user && user.id) {
      setAccessReady(false);
      setAccessError("");
      apiFetch(`${getAPIUrl()}/users/user_access/me`, {
        cache: 'no-store',
        signal: accessController.signal,
      })
        .then((res) => {
          if (!res.ok) throw new Error(`Не удалось загрузить права: ${res.status}`);
          return res.json();
        })
        .then(data => {
          if (!cancelled) setAllowedPages(getAllowedPages(data));
        })
        .catch((error) => {
          console.error("Ошибка загрузки прав пользователя:", error);
          if (!cancelled) {
            setAllowedPages([]);
            setAccessError(
              error?.name === "AbortError"
                ? "Сервер не ответил при загрузке прав. Обновите страницу."
                : "Не удалось загрузить права доступа. Обновите страницу или обратитесь к администратору."
            );
          }
        })
        .finally(() => {
          window.clearTimeout(accessTimeoutId);
          if (!cancelled) setAccessReady(true);
        });
    } else if (!user) {
      setAllowedPages([]);
      setAccessError("");
      setAccessReady(false);
    } else {
      setAllowedPages([]);
      setAccessError("Данные текущего пользователя повреждены. Выйдите из системы и войдите снова.");
      setAccessReady(true);
    }

    return () => {
      cancelled = true;
      accessController.abort();
      window.clearTimeout(accessTimeoutId);
    };
  }, [user]);

  const fallbackPath = getFallbackPath(allowedPages, user);
  const accessRouteProps = { allowedPages, accessReady, fallbackPath };

  return (
    <Router>
      <div className="App">
        {user && accessReady && <Navbar user={user} setUser={setUser} allowedPages={allowedPages} />}
        <ConditionalGlobalFiltersBar user={user} accessReady={accessReady}/>
        <SessionExpiredDialog isOpen={isSessionExpired} onConfirm={handleSessionExpiredConfirm} />
        <div className="container mt-2 w-100">
          <Routes>
            <Route path="/login" element={<Login setUser={setUser} />} />
            <Route path="/register" element={<Register setUser={setUser} />} />
            <Route path="/access-denied" element={<AccessDenied accessError={accessError} />} />
            <Route
              path="/stat"
              element={
                <ProtectedRoute page={PAGE_KEYS.STATISTICS} {...accessRouteProps}>
                  <Home user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedRoute page={PAGE_KEYS.DYNAMICS} {...accessRouteProps}>
                  <DynamicsAnalysis allowedPages={allowedPages} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/trager-dynamics"
              element={
                <ProtectedRoute page={PAGE_KEYS.DYNAMICS} {...accessRouteProps}>
                  <DynamicsAnalysis allowedPages={allowedPages} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dynamics-context"
              element={
                <ProtectedRoute page={[PAGE_KEYS.DYNAMICS, PAGE_KEYS.DYNAMICS_CONTEXT]} {...accessRouteProps}>
                  <DynamicsAnalysis allowedPages={allowedPages} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/season-index"
              element={
                <ProtectedRoute page={[PAGE_KEYS.DYNAMICS, PAGE_KEYS.DYNAMICS_SEASON]} {...accessRouteProps}>
                  <DynamicsAnalysis allowedPages={allowedPages} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/time-series"
              element={
                <ProtectedRoute page={[PAGE_KEYS.DYNAMICS, PAGE_KEYS.DYNAMICS_TIME_SERIES]} {...accessRouteProps}>
                  <DynamicsAnalysis allowedPages={allowedPages} />
                </ProtectedRoute>
              }
            />

            
            <Route
              path="/pol"
              element={
                <ProtectedRoute page={PAGE_KEYS.USERS} {...accessRouteProps}>
                  <CentralitiesTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/glo"
              element={
                <ProtectedRoute page={PAGE_KEYS.GLOSSARY} {...accessRouteProps}>
                  <Glos />
                </ProtectedRoute>
              }
            />

            <Route
              path="/AnPa"
              element={
                <AdminRoute page={[PAGE_KEYS.ADMINISTRATION, PAGE_KEYS.ANALYSIS]} {...accessRouteProps}>
                  <AnalysisPage />
                </AdminRoute>
              }
            />
            <Route
              path="/Papa"
              element={
                <AdminRoute page={[PAGE_KEYS.ADMINISTRATION, PAGE_KEYS.PARSING]} {...accessRouteProps}>
                  <ParsingPage />
                </AdminRoute>
              }
            />
            <Route
              path="/DatabaseAdmin"
              element={
                <AdminRoute page={[PAGE_KEYS.ADMINISTRATION, PAGE_KEYS.DATABASE_ADMIN]} {...accessRouteProps}>
                  <DatabaseAdmin/>
                </AdminRoute>
              }
            />
            <Route
              path="/dictionaries"
              element={
                <AdminRoute page={[PAGE_KEYS.ADMINISTRATION, PAGE_KEYS.DICTIONARIES]} {...accessRouteProps}>
                  <DictionariesAdmin />
                </AdminRoute>
              }
            />
            <Route
              path="/diss"
              element={
                <ProtectedRoute page={[PAGE_KEYS.DISCUSSIONS, PAGE_KEYS.DISCUSSIONS_ACTIVITY]} {...accessRouteProps}>
                  <DisscussionsAnalysis allowedPages={allowedPages} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/diss/community-activity"
              element={
                <ProtectedRoute page={[PAGE_KEYS.DISCUSSIONS, PAGE_KEYS.DISCUSSIONS_COMMUNITY]} {...accessRouteProps}>
                  <DisscussionsAnalysis allowedPages={allowedPages} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/diss/anomaly"
              element={
                <ProtectedRoute page={[PAGE_KEYS.DISCUSSIONS, PAGE_KEYS.DISCUSSIONS_ANOMALY]} {...accessRouteProps}>
                  <DisscussionsAnalysis allowedPages={allowedPages} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/diss/markers"
              element={
                <ProtectedRoute page={[PAGE_KEYS.DISCUSSIONS, PAGE_KEYS.DISCUSSIONS_MARKERS]} {...accessRouteProps}>
                  <DisscussionsAnalysis allowedPages={allowedPages} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/diss/disscussions"
              element={
                <ProtectedRoute page={[PAGE_KEYS.DISCUSSIONS, PAGE_KEYS.DISCUSSIONS_DETAILS]} {...accessRouteProps}>
                  <DisscussionsAnalysis allowedPages={allowedPages} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/text-markers"
              element={
                <ProtectedRoute page={PAGE_KEYS.PSYCHOLINGUISTIC} {...accessRouteProps}>
                  <TextMarkersAnalysis />
                </ProtectedRoute>
              }
            />
            <Route
              path="/emotive-analysis"
              element={
                <ProtectedRoute page={PAGE_KEYS.EMOTIVE} {...accessRouteProps}>
                  <EmotiveAnalysis allowedPages={allowedPages} />
                </ProtectedRoute>
              }
            />
            <Route
                path="/topics-analysis"
                element={
                  <ProtectedRoute page={PAGE_KEYS.THEMATIC} {...accessRouteProps}>
                    <TopicsAnalysis allowedPages={allowedPages} />
                  </ProtectedRoute>
                }
            />
            <Route
              path="/admin"
              element={
                <AdminRoute page={PAGE_KEYS.ADMINISTRATION} {...accessRouteProps}>
                  <Admin allowedPages={allowedPages} />
                </AdminRoute>
              }
            />
            <Route
              path="/source-data"
              element={
                <AdminRoute page={[PAGE_KEYS.ADMINISTRATION, PAGE_KEYS.SOURCE_DATA]} {...accessRouteProps}>
                  <Navigate to="/admin?tab=source-data" replace />
                </AdminRoute>
              }
            />
            <Route
              path="/export-data"
              element={
                <AdminRoute page={[PAGE_KEYS.ADMINISTRATION, PAGE_KEYS.EXPORT_DATA]} {...accessRouteProps}>
                  <Navigate to="/admin?tab=export-data" replace />
                </AdminRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <AdminRoute page={[PAGE_KEYS.ADMINISTRATION, PAGE_KEYS.REPORTS]} {...accessRouteProps}>
                  <Navigate to="/admin?tab=reports" replace />
                </AdminRoute>
              }
            />
            <Route
              path="/posts-search"
              element={
                <ProtectedRoute page={PAGE_KEYS.POSTS_SEARCH} {...accessRouteProps}>
                  <PostsSearch />
                </ProtectedRoute>
              }
            />
          </Routes>
        </div>

       
      </div>
    </Router>
  );
}

export default App;
